import { yupResolver } from '@hookform/resolvers/yup'
import { object, string } from '@if/core/yup'
import { ControlledTextField } from '@if/mui'
import SearchIcon from '@mui/icons-material/Search'
import { Button, Stack } from '@mui/material'
import { InteracWireSearchPossibleMatchFilters } from '@src/api/interac-wire-api'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface InteracWireMatchLoanFiltersProps {
  onFiltersChange: (filters: InteracWireSearchPossibleMatchFilters | null) => void
  defaultValues?: InteracWireSearchPossibleMatchFilters | null
  autofocus?: boolean
  disabled?: boolean
}

const InteracWireMatchLoanFilters = ({
  onFiltersChange,
  defaultValues: pDefaultValues,
  autofocus = false,
  disabled = false,
}: InteracWireMatchLoanFiltersProps) => {
  const { t } = useTranslation()
  const resolver = useMemo(
    () =>
      yupResolver(
        object({
          search: string()
            .required()
            .test('is-email-or-number', t('interacWire.searchByEmailOrReferenceError'), (value) => {
              if (!value) return false
              // Check if the value is a valid email or a number
              return /^[0-9]+$/.test(value) || string().email().isValidSync(value)
            }),
        }),
      ),
    [t],
  )

  const defaultValues = useMemo(
    () => ({
      search: pDefaultValues?.referenceNumber ?? pDefaultValues?.email ?? '',
    }),
    [pDefaultValues],
  )

  const { control, handleSubmit } = useForm({
    resolver,
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const onSubmit = handleSubmit((data) => {
    if (disabled) return
    if (!data.search) {
      onFiltersChange(null)
      return
    }
    const isRefNumber = /^[0-9]+$/.exec(data.search) !== null
    onFiltersChange({
      email: !isRefNumber ? data.search : null,
      referenceNumber: isRefNumber ? data.search : null,
    })
  })

  return (
    <Stack gap={1} direction="row" component="form" alignItems="start" onSubmit={onSubmit}>
      <ControlledTextField
        control={control}
        name="search"
        label={t('interacWire.searchByEmailOrReference')}
        fullWidth={false}
        sx={{ width: 400 }}
        size="small"
        autoFocus={autofocus}
        disabled={disabled}
      />
      <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={disabled}>
        {t('common.search')}
      </Button>
    </Stack>
  )
}

export default InteracWireMatchLoanFilters
